<template>
  <div class="image-trail">
    <template v-for="(image, index) in loadedImages">
      <img class="image-trail__image" :src="image" :key="index" />
    </template>
  </div>
</template>

<script lang="js">
import preloadImages from '../../../js/util/preloadImages';
import reflow from '../../../js/util/reflow';
import lerp from './util/lerp';
import getDistance from './util/getDistance';

// minimum mouse distance to show the next image
const THRESHOLD = 150;

// the percentage of the distance to interpolate between two mouse positions
const LERP_AMOUNT = 0.1;

export default  {
  name: 'ahc-image-trail',
  props: {
    images: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loadedImages: []
    }
  },
  methods: {
    render() {
      // cache previous mouse position
      this.cachedMousePosition = {
        x: lerp(this.cachedMousePosition.x || this.mousePosition.x, this.mousePosition.x, LERP_AMOUNT),
        y: lerp(this.cachedMousePosition.y || this.mousePosition.y, this.mousePosition.y, LERP_AMOUNT)
      };

      // render the next image if the mouse has moved far enough
      const distance = getDistance(this.mousePosition, this.lastMousePosition);

      if (distance > THRESHOLD) {
        this.showNextImage();

        this.nextZIndex = this.nextZIndex + 1;
        this.nextIndex = (this.nextIndex + 1) % this.loadedImages.length;

        this.lastMousePosition = this.mousePosition;
      }

      // reset the z-index value if no images are currently animating
      let isIdle = true;
      for (let i = 0; i < this.loadedImagesEls.length; i++) {
        if (!this.loadedImagesEls[i].idle) {
          isIdle = false;
          break;
        }
      }

      if (isIdle && this.nextZIndex !== 1) {
        this.nextZIndex = 1;
      }

      // loop...
      this.raf = window.requestAnimationFrame(() => this.render());
    },
    showNextImage() {
      const index = this.nextIndex;
      const img = this.loadedImagesEls[index].el;
      const { width, height } = img.getBoundingClientRect();

      // top offset of the contanining element, to adjust the mouse position;
      const offsetTop = this.$el.getBoundingClientRect().top + window.pageYOffset;

      // initial position, centered on a linearly interpolated point between the current and last mouse positions
      img.style.opacity = 1;
      img.style.zIndex = this.nextZIndex;
      img.style.transform = `translate3d(${this.cachedMousePosition.x - width/2}px, ${this.cachedMousePosition.y - offsetTop - height/2}px, 0)`;
      img.style.visibility = 'visible';
      img.style.transition = 'none';

      // set the image to "idle" when it's done animating
      const onTransitionEnd = (e) => {
        if (e.target === img) {
          this.loadedImagesEls[index].idle = true;
          img.removeEventListener('transitionend', onTransitionEnd, false);
        }
      };

      this.loadedImagesEls[index].idle = false;
      img.addEventListener('transitionend', onTransitionEnd, false);

      // this forces a sync layout so that the previously set styles
      // take effect before we set the new ones so the transition will
      // apply
      reflow(img);

      // the new position, centered on the last-recorded mouse position
      img.style.transform = `translate3d(${this.mousePosition.x - width/2}px, ${this.mousePosition.y - offsetTop - height/2}px, 0)`;
      img.style.opacity = 0;
      img.style.visibility = 'hidden';
      img.style.transition = `transform 1s ease-out, opacity 0.5s ease-out 0.5s, visibility 0s linear 1s`;
    },
    onMouseMove(e) {
      this.mousePosition = {
        x: e.pageX,
        y: e.pageY
      };
    }
  },
  created() {
    // last recorded position of the mouse
    this.mousePosition = { x: 0, y: 0 };

    // interpolated point between the current and previous mouse positions
    this.cachedMousePosition = { x: 0, y: 0 };

    // the mouse position when the previous image was rendered
    this.lastMousePosition = { x: 0, y: 0 };

    // the index of the next image to render
    this.nextIndex = 0;

    // the z-index to apply to the next image
    this.nextZIndex = 1;
  },
  mounted() {
    // do not apply the effect on devices that don't support hover with their
    // primary input device, (assume touch), or if the user prefers reduced motion
    if (
      !window.matchMedia('(hover: hover)').matches ||
      window.matchMedia('(prefers-reduced-motion)').matches
    ) {
      return;
    }

    preloadImages(this.images).then(srcs => {
      this.loadedImages = srcs;

      this.$nextTick(() => {
        this.loadedImagesEls = Array.prototype.slice.call(this.$el.querySelectorAll('.image-trail__image'))
          .map(el => ({ el, idle: true }));

        this.$el.addEventListener('mousemove', this.onMouseMove, false);
        this.raf = window.requestAnimationFrame(() => this.render());
      });
    });
  },
  beforeDestroy() {
    this.$el.removeEventListener('mousemove', this.onMouseMove, false);
    window.cancelAnimationFrame(this.raf);
  }
}
</script>

<style lang="scss">
@import "image-trail";
</style>
